import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../api/api';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchSupplierProducts, fetchSuppliers } from '../../store/slices/productSlice';
import { fetchEventOrders } from '../../store/slices/eventOrderSlice';
import { PurchaseWorksheet, PurchaseWorksheetItem, PurchaseWorksheetTraceability, PurchaseWorksheetTraceabilityItem, SupplierQualityAnalytics } from '../../types';
import SearchSelect from '../../components/SearchSelect';
import { openAndPrintHtml } from '../../utils/printDocument';
import { useSupplierDataScope } from '../../hooks/useSupplierDataScope';
const thStyle: React.CSSProperties = { padding: '8px', textAlign: 'left', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-border)' };
const tdStyle: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid var(--crm-table-row-border)' };
type ButtonTone = 'success' | 'info' | 'danger' | 'warning' | 'muted' | 'accent';
const btn = (tone: ButtonTone, extra: React.CSSProperties = {}): React.CSSProperties => ({
  background: tone === 'muted' || tone === 'accent' ? 'var(--crm-surface-alt)' : `var(--crm-${tone}-bg)`,
  color: tone === 'muted' ? 'var(--crm-text-muted)' : `var(--crm-${tone})`,
  border: `1px solid var(--crm-${tone === 'muted' ? 'border' : tone === 'accent' ? 'accent' : tone})`,
  borderRadius: 'var(--crm-radius-md)',
  padding: '8px 16px',
  cursor: 'pointer',
  ...extra,
});
const fieldStyle: React.CSSProperties = {
  color: 'var(--crm-text)',
  background: 'var(--crm-input-bg)',
  border: '1px solid var(--crm-border)',
  borderRadius: 'var(--crm-radius-md)',
};
const statusLabels: Record<string, string> = {
  Draft: 'Чернетка',
  Approved: 'Погоджено',
  Posted: 'Проведено',
  Cancelled: 'Скасовано',
};
const PurchaseWorksheetPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { suppliers, supplierProducts, groupId, epoch, isCurrent } = useSupplierDataScope();
  const { orders } = useAppSelector((s) => s.eventOrder);
  const [worksheets, setWorksheets] = useState<PurchaseWorksheet[]>([]);
  const [selected, setSelected] = useState<PurchaseWorksheet | null>(null);
  const [loading, setLoading] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [checkedOrders, setCheckedOrders] = useState<Set<number>>(new Set());
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  // Local editable state for the open worksheet's items
  const [rows, setRows] = useState<Record<number, { adjustedQuantity: string; selectedSupplierId: string; supplierQuery: string; isExcluded: boolean; notes: string }>>({});
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [exportSupplierId, setExportSupplierId] = useState('');
  const [exportCategory, setExportCategory] = useState('');
  const [exportIncludeExcluded, setExportIncludeExcluded] = useState(false);
  const [exportOnlyUnassignedSupplier, setExportOnlyUnassignedSupplier] = useState(false);
  const [quantityBasis, setQuantityBasis] = useState<'brutto' | 'netto' | 'output'>('brutto');
  const [printColumns, setPrintColumns] = useState({
    category: true,
    product: true,
    unit: true,
    quantity: true,
    supplier: true,
  });
  const [autoSaving, setAutoSaving] = useState(false);
  const [supplierQuality, setSupplierQuality] = useState<SupplierQualityAnalytics[]>([]);
  const [traceability, setTraceability] = useState<PurchaseWorksheetTraceability | null>(null);
  const [traceabilityItem, setTraceabilityItem] = useState<PurchaseWorksheetTraceabilityItem | null>(null);
  const [traceabilityLoading, setTraceabilityLoading] = useState(false);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressAutoSaveRef = useRef(false);
  const parseAdjustment = (value: string) => {
    const normalized = value.replace(',', '.').trim();
    if (!normalized || normalized === '+' || normalized === '-') return 0;
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const formatAdjustment = (value: number) => {
    const normalized = Number.isFinite(value) ? Number(value.toFixed(6)) : 0;
    return normalized === 0 ? '0' : String(normalized);
  };
  const getApiErrorMessage = (error: any, fallback: string) => {
    const responseData = error?.response?.data;
    if (typeof responseData === 'string' && responseData.trim()) return responseData;
    if (responseData?.message) return String(responseData.message);
    if (responseData?.title) return String(responseData.title);
    if (responseData?.errors && typeof responseData.errors === 'object') {
      const combined = Object.values(responseData.errors).flat().filter(Boolean).join('; ');
      if (combined) return combined;
    }
    return error?.message || fallback;
  };
  const getRequiredWithAdjustment = (item: PurchaseWorksheetItem, row: { adjustedQuantity: string }) =>
    item.requiredQuantity + parseAdjustment(row.adjustedQuantity);
  const getUnitLabel = (weightType: string) => {
    const normalized = (weightType || '').trim().toLowerCase();
    if (normalized === 'kg' || normalized === 'кг') return 'кг';
    if (normalized === 'g' || normalized === 'гр' || normalized === 'г') return 'г';
    if (normalized === 'l' || normalized === 'л') return 'л';
    if (normalized === 'ml' || normalized === 'мл') return 'мл';
    if (normalized === 'pcs' || normalized === 'pc' || normalized === 'шт' || normalized === 'шт.') return 'шт';
    return weightType || '—';
  };
  const allSupplierSearchItems = useMemo(
    () => suppliers.map((supplier) => ({ id: String(supplier.id), name: supplier.name })),
    [suppliers]
  );
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<PurchaseWorksheet[]>('/purchaseworksheets');
      setWorksheets(res.data);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    dispatch(fetchEventOrders());
    api.get<SupplierQualityAnalytics[]>('/suppliers/quality-analytics')
      .then(response => setSupplierQuality(response.data))
      .catch(() => setSupplierQuality([]));
    loadList();
  }, [dispatch, loadList]);
  useEffect(() => {
    if (!isCurrent || groupId === null) return;
    dispatch(fetchSuppliers({ groupId, epoch }));
    dispatch(fetchSupplierProducts({ groupId, epoch }));
  }, [dispatch, epoch, groupId, isCurrent]);
  const openWorksheet = async (id: number) => {
    const res = await api.get<PurchaseWorksheet>(`/purchaseworksheets/${id}`);
    setSelected(res.data);
    const nextBasis = (res.data as any)?.quantityBasis === 'netto' || (res.data as any)?.quantityBasis === 'output' ? (res.data as any).quantityBasis : 'brutto';
    setQuantityBasis(nextBasis);
    setExportSupplierId('');
    setExportCategory('');
    setExportIncludeExcluded(false);
    setExportOnlyUnassignedSupplier(false);
    const map: Record<number, { adjustedQuantity: string; selectedSupplierId: string; supplierQuery: string; isExcluded: boolean; notes: string }> = {};
    (res.data.items || []).forEach((i) => {
      map[i.id] = {
        adjustedQuantity: formatAdjustment(i.adjustedQuantity - i.requiredQuantity),
        selectedSupplierId: i.selectedSupplierId ? String(i.selectedSupplierId) : '',
        supplierQuery: i.selectedSupplierName || '',
        isExcluded: i.isExcluded,
        notes: i.notes || '',
      };
    });
    suppressAutoSaveRef.current = true;
    setRows(map);
  };
  const closeWorksheet = () => {
    setSelected(null);
    setRows({});
    setExportSupplierId('');
    setExportCategory('');
    setExportIncludeExcluded(false);
    setExportOnlyUnassignedSupplier(false);
    setGenerateError(null);
    setTraceability(null);
    setTraceabilityItem(null);
  };
  const openTraceability = async (item: PurchaseWorksheetItem) => {
    if (!selected) return;
    setTraceabilityLoading(true);
    try {
      const response = traceability
        ? { data: traceability }
        : await api.get<PurchaseWorksheetTraceability>(`/purchaseworksheets/${selected.id}/traceability`);
      setTraceability(response.data);
      setTraceabilityItem(response.data.items.find(entry => entry.itemId === item.id) || null);
    } catch (error: any) {
      window.alert(getApiErrorMessage(error, 'Не вдалося завантажити розшифровку продукту.'));
    } finally {
      setTraceabilityLoading(false);
    }
  };
  const toggleOrder = (id: number) => {
    setCheckedOrders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const handleGenerate = async () => {
    if (checkedOrders.size === 0) {
      window.alert('Оберіть хоча б одне замовлення.');
      return;
    }
    setGenerating(true);
    setGenerateError(null);
    try {
      const payload = {
        name: newName || `Закупівельний лист ${new Date().toLocaleDateString('uk-UA')}`,
        notes: newNotes,
        eventOrderIds: Array.from(checkedOrders),
        quantityBasis,
      };
      const res = await api.post<PurchaseWorksheet>('/purchaseworksheets/generate', payload);
      setShowNewModal(false);
      setNewName('');
      setNewNotes('');
      setCheckedOrders(new Set());
      await loadList();
      await openWorksheet(res.data.id);
    } catch (error: any) {
      setGenerateError(getApiErrorMessage(error, 'Не вдалося сформувати закупівельний лист. Спробуйте ще раз або зменште кількість замовлень.'));
    } finally {
      setGenerating(false);
    }
  };
  const handleDeleteWorksheet = async (id: number) => {
    if (!window.confirm('Видалити закупівельний лист?')) return;
    const worksheet = worksheets.find(item => item.id === id);
    if (!worksheet) return;
    await api.delete(`/purchaseworksheets/${id}`, { headers: { 'If-Match': `"${worksheet.version}"` } });
    if (selected?.id === id) closeWorksheet();
    await loadList();
  };
  const handleStatusChange = async (status: 'Approved' | 'Posted' | 'Cancelled' | 'Draft') => {
    if (!selected) return;
    const label = statusLabels[status].toLowerCase();
    const confirmMessage = status === 'Draft'
      ? 'Повернути скасований закупівельний лист у роботу як чернетку?'
      : `Змінити статус документа на «${label}»?`;
    if (!window.confirm(confirmMessage)) return;
    try {
      let current = selected;
      if (current.status === 'Draft' && status === 'Approved') {
        const saved = await persistChanges(true);
        if (!saved) return;
        current = saved;
      }
      const response = await api.post<PurchaseWorksheet>(
        `/purchaseworksheets/${current.id}/status`,
        { status },
        { headers: { 'If-Match': `"${current.version}"` } }
      );
      setSelected(response.data);
      await loadList();
    } catch (error: any) {
      window.alert(getApiErrorMessage(error, 'Не вдалося змінити статус документа.'));
    }
  };
  const handleCreateSupplierOrders = async () => {
    if (!selected) return;
    const expected = new Date();
    expected.setDate(expected.getDate() + 3);
    if (!window.confirm(`Створити окремі замовлення постачальникам з очікуваною датою ${expected.toLocaleDateString('uk-UA')}?`)) return;
    setSaving(true);
    try {
      await api.post(
        `/purchaseworksheets/${selected.id}/supplier-orders`,
        { expectedDeliveryDate: expected.toISOString(), notes: selected.notes },
        {
          headers: {
            'If-Match': `"${selected.version}"`,
            'Idempotency-Key': `supplier-orders-${selected.id}-${Date.now()}`,
          },
        },
      );
      window.location.assign('/management/supplier-purchase-orders');
    } catch (error: any) {
      window.alert(getApiErrorMessage(error, 'Не вдалося створити замовлення постачальникам.'));
    } finally {
      setSaving(false);
    }
  };
  const updateRow = (itemId: number, patch: Partial<{ adjustedQuantity: string; selectedSupplierId: string; supplierQuery: string; isExcluded: boolean; notes: string }>) => {
    setRows((r) => ({ ...r, [itemId]: { ...r[itemId], ...patch } }));
  };
  const getAllowedSuppliers = (item: PurchaseWorksheetItem) => {
    const sortOffers = (items: typeof suppliers) => [...items].sort((a, b) => {
      const aProfile = supplierQuality.find(profile => profile.supplierId === a.id);
      const bProfile = supplierQuality.find(profile => profile.supplierId === b.id);
      const riskRank = (risk?: string) => risk === 'normal' || !risk ? 0 : risk === 'warning' ? 1 : 2;
      const riskDifference = riskRank(aProfile?.riskLevel) - riskRank(bProfile?.riskLevel);
      if (riskDifference !== 0) return riskDifference;
      const effectivePrice = (supplierId: number) =>
        supplierProducts.find(product =>
          product.isActive &&
          product.internalProductId === item.productId &&
          product.supplierId === supplierId)?.effectiveUnitPrice ?? Number.MAX_SAFE_INTEGER;
      const adjustedPrice = (supplierId: number, profile?: SupplierQualityAnalytics) => {
        const unitPrice = effectivePrice(supplierId);
        const score = profile?.procurementScore ?? 100;
        return unitPrice * (1 + (100 - score) / 100 * 0.2 + (profile?.riskLevel === 'critical' ? 0.15 : 0));
      };
      return adjustedPrice(a.id, aProfile) - adjustedPrice(b.id, bProfile);
    });
    const universalSupplierIds = new Set(
      suppliers
        .filter((s) => /універсал|universal/i.test(s.name))
        .map((s) => s.id)
    );
    if (!item.productId) {
      return sortOffers(suppliers);
    }
    const productSupplierIds = new Set(
      supplierProducts
        .filter((sp) => sp.isActive && sp.internalProductId === item.productId)
        .map((sp) => sp.supplierId)
    );
    suppliers.forEach((s) => {
      if (universalSupplierIds.has(s.id)) {
        productSupplierIds.add(s.id);
      }
    });
    if (productSupplierIds.size === 0) {
      return sortOffers(suppliers);
    }
    return sortOffers(suppliers.filter((s) => productSupplierIds.has(s.id)));
  };
  const handleRegenerate = async () => {
    if (!selected) return;
    if (!window.confirm('Переформувати закупівельний лист? Кількості перерахуються по актуальних рецептах. Ваші коригування по постачальниках/примітках буде збережено.')) return;
    setRegenerating(true);
    setGenerateError(null);
    try {
      const res = await api.post<PurchaseWorksheet>(
        `/purchaseworksheets/${selected.id}/regenerate`,
        { quantityBasis },
        { headers: { 'If-Match': `"${selected.version}"` } }
      );
      setSelected(res.data);
      setQuantityBasis((res.data as any)?.quantityBasis === 'netto' || (res.data as any)?.quantityBasis === 'output' ? (res.data as any).quantityBasis : 'brutto');
      const map: Record<number, { adjustedQuantity: string; selectedSupplierId: string; supplierQuery: string; isExcluded: boolean; notes: string }> = {};
      (res.data.items || []).forEach((i) => {
        map[i.id] = {
          adjustedQuantity: formatAdjustment(i.adjustedQuantity - i.requiredQuantity),
          selectedSupplierId: i.selectedSupplierId ? String(i.selectedSupplierId) : '',
          supplierQuery: i.selectedSupplierName || '',
          isExcluded: i.isExcluded,
          notes: i.notes || '',
        };
      });
      suppressAutoSaveRef.current = true;
      setRows(map);
      await loadList();
    } catch (error: any) {
      setGenerateError(getApiErrorMessage(error, 'Не вдалося переформувати закупівельний лист. Спробуйте ще раз.'));
    } finally {
      setRegenerating(false);
    }
  };
  const persistChanges = async (showSavingIndicator: boolean): Promise<PurchaseWorksheet | null> => {
    if (!selected || selected.status !== 'Draft') return null;
    if (showSavingIndicator) {
      setSaving(true);
    } else {
      setAutoSaving(true);
    }
    try {
      const requiredById = new Map((selected.items || []).map((i) => [i.id, i.requiredQuantity]));
      const payload = Object.entries(rows).map(([itemId, r]) => {
        const id = parseInt(itemId, 10);
        const required = requiredById.get(id) || 0;
        return {
          worksheetItemId: id,
          adjustedQuantity: required + parseAdjustment(r.adjustedQuantity),
          selectedSupplierId: r.selectedSupplierId ? parseInt(r.selectedSupplierId, 10) : null,
          isExcluded: r.isExcluded,
          notes: r.notes,
        };
      });
      const res = await api.put<PurchaseWorksheet>(
        `/purchaseworksheets/${selected.id}/items`,
        payload,
        { headers: { 'If-Match': `"${selected.version}"` } }
      );
      setSelected(res.data);
      const map: Record<number, { adjustedQuantity: string; selectedSupplierId: string; supplierQuery: string; isExcluded: boolean; notes: string }> = {};
      (res.data.items || []).forEach((i) => {
        map[i.id] = {
          adjustedQuantity: formatAdjustment(i.adjustedQuantity - i.requiredQuantity),
          selectedSupplierId: i.selectedSupplierId ? String(i.selectedSupplierId) : '',
          supplierQuery: i.selectedSupplierName || '',
          isExcluded: i.isExcluded,
          notes: i.notes || '',
        };
      });
      suppressAutoSaveRef.current = true;
      setRows(map);
      if (showSavingIndicator) {
        await loadList();
      }
      setGenerateError(null);
      return res.data;
    } catch (error: any) {
      setGenerateError(getApiErrorMessage(error, 'Не вдалося зберегти зміни. Оновіть документ і повторіть дію.'));
      throw error;
    } finally {
      if (showSavingIndicator) {
        setSaving(false);
      } else {
        setAutoSaving(false);
      }
    }
  };
  const handleSaveChanges = async () => {
    await persistChanges(true).catch(() => undefined);
  };
  useEffect(() => {
    if (!selected || selected.status !== 'Draft') return;
    if (suppressAutoSaveRef.current) {
      suppressAutoSaveRef.current = false;
      return;
    }
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(() => {
      persistChanges(false).catch(() => undefined);
    }, 800);
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, selected?.id]);
  const handleExportExcel = async () => {
    if (!selected) return;
    const res = await api.get(`/purchaseworksheets/${selected.id}/export-excel`, {
      responseType: 'blob',
      params: {
        supplierId: exportSupplierId ? parseInt(exportSupplierId) : undefined,
        category: exportCategory || undefined,
        includeExcluded: exportIncludeExcluded || undefined,
      },
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Закупівельний_лист_${selected.id}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };
  const handleExportText = () => {
    if (!selected) return;

    const items = getExportPrintItems();
    if (items.length === 0) {
      window.alert('Немає позицій для експорту.');
      return;
    }

    const byCategory = new Map<string, typeof items>();
    items.forEach((item) => {
      const category = item.categoryName || 'Без категорії';
      if (!byCategory.has(category)) {
        byCategory.set(category, []);
      }
      byCategory.get(category)!.push(item);
    });

    const lines: string[] = [
      `${selected.name}`,
      `Дата: ${new Date().toLocaleString('uk-UA')}`,
      `Фільтр категорії: ${exportCategory || 'Усі'}`,
      '',
    ];

    Array.from(byCategory.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([category, categoryItems]) => {
        lines.push(`📦 ${category}`);
        categoryItems.forEach((item, index) => {
          const quantity = Number.isInteger(item.quantity) ? item.quantity.toString() : item.quantity.toFixed(2);
          lines.push(`${index + 1}. ${item.productName} — ${quantity} ${getUnitLabel(item.weightType)}`);
        });
        lines.push('');
      });

    const exportSupplierName = exportSupplierId ? (suppliers.find((s) => String(s.id) === exportSupplierId)?.name || '—') : 'Усі';
    lines.splice(1, 0, `Постачальник: ${exportSupplierName}`);
    const blob = new Blob([lines.join('\r\n')], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const safeSupplierName = exportSupplierName.replace(/[\\/:*?"<>|]+/g, '_').trim() || 'Усі';
    const safeDate = new Date().toLocaleDateString('uk-UA').replace(/[\\/:*?"<>|.\s]+/g, '-');
    link.setAttribute('download', `Закупівельний_лист_${selected.id}_${safeDate}_${safeSupplierName}_Viber.txt`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };
  const getExportPrintItems = () => {
    const supplierFilterId = exportSupplierId ? parseInt(exportSupplierId) : null;
    return (selected?.items || [])
      .filter((item) => {
        const row = rows[item.id];
        const isExcluded = row?.isExcluded ?? item.isExcluded;
        const selectedSupplierId = row?.selectedSupplierId ? parseInt(row.selectedSupplierId) : item.selectedSupplierId;
        if (isExcluded && !exportIncludeExcluded) return false;
        if (exportOnlyUnassignedSupplier) {
          if (selectedSupplierId) return false;
        } else if (supplierFilterId && selectedSupplierId !== supplierFilterId) {
          return false;
        }
        if (exportCategory && item.categoryName !== exportCategory) return false;
        return true;
      })
      .map((item) => {
        const row = rows[item.id];
        const supplierId = row?.selectedSupplierId ? parseInt(row.selectedSupplierId) : item.selectedSupplierId;
        const supplierName = suppliers.find((s) => s.id === supplierId)?.name || item.selectedSupplierName || '—';
        const quantity = getRequiredWithAdjustment(item, row || { adjustedQuantity: '0' });
        return { ...item, supplierName, quantity };
      });
  };
  const filteredVisibleItems = useMemo(() => {
    const supplierFilterId = exportSupplierId ? parseInt(exportSupplierId) : null;
    const supplierFilterQuery = exportSupplierId.trim().toLowerCase();
    return (selected?.items || []).filter((item) => {
      const row = rows[item.id];
      const isExcluded = row?.isExcluded ?? item.isExcluded;
      const selectedSupplierId = row?.selectedSupplierId ? parseInt(row.selectedSupplierId) : item.selectedSupplierId;
      const selectedSupplierName = row?.supplierQuery || item.selectedSupplierName || '';
      if (isExcluded && !exportIncludeExcluded) return false;
      if (exportOnlyUnassignedSupplier) {
        if (selectedSupplierId) return false;
      } else if (supplierFilterQuery) {
        if (!selectedSupplierName.toLowerCase().includes(supplierFilterQuery) && selectedSupplierId !== supplierFilterId) return false;
      } else if (supplierFilterId && selectedSupplierId !== supplierFilterId) {
        return false;
      }
      if (exportCategory && item.categoryName !== exportCategory) return false;
      return true;
    });
  }, [selected, rows, exportSupplierId, exportOnlyUnassignedSupplier, exportCategory, exportIncludeExcluded]);
  const handlePrint = () => {
    if (!selected) return;
    const printItems = getExportPrintItems();
    const enabledColumns = [
      { key: 'category', label: '\u041a\u0430\u0442\u0435\u0433\u043e\u0440\u0456\u044f' },
      { key: 'product', label: '\u041f\u0440\u043e\u0434\u0443\u043a\u0442' },
      { key: 'unit', label: '\u041e\u0434\u0438\u043d\u0438\u0446\u044f' },
      { key: 'quantity', label: '\u041a\u0456\u043b\u044c\u043a\u0456\u0441\u0442\u044c' },
      { key: 'supplier', label: '\u041f\u043e\u0441\u0442\u0430\u0447\u0430\u043b\u044c\u043d\u0438\u043a' },
    ].filter((c) => printColumns[c.key as keyof typeof printColumns]);
    const headerHtml = enabledColumns.map((column) => `<th>${column.label}</th>`).join('');
    const rowsHtml = printItems
      .map((item) => {
        const cells: string[] = [];
        if (printColumns.category) cells.push(`<td>${item.categoryName || ''}</td>`);
        if (printColumns.product) cells.push(`<td>${item.productName}</td>`);
        if (printColumns.unit) cells.push(`<td style="text-align:center;">${getUnitLabel(item.weightType)}</td>`);
        if (printColumns.quantity) cells.push(`<td style="text-align:right;">${item.quantity.toFixed(2)}</td>`);
        if (printColumns.supplier) cells.push(`<td>${item.supplierName}</td>`);
        return `<tr>${cells.join('')}</tr>`;
      })
      .join('');
    void openAndPrintHtml(`
      <html>
        <head>
          <title>${selected.name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            body, body * { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            h2 { margin: 0 0 8px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            th, td { border: 1px solid #ccc; padding: 8px; font-size: 12px; }
            th { background: #f2f2f2; text-align: left; }
            .meta { color: #444; margin-bottom: 4px; }
          </style>
        </head>
        <body>
          <h2>${selected.name}</h2>
          <div class="meta">\u0414\u0430\u0442\u0430: ${new Date(selected.createdAt).toLocaleString('uk-UA')}</div>
          <div class="meta">\u0424\u0456\u043b\u044c\u0442\u0440 \u043f\u043e\u0441\u0442\u0430\u0447\u0430\u043b\u044c\u043d\u0438\u043a\u0430: ${exportOnlyUnassignedSupplier ? 'Без постачальника' : (exportSupplierId ? (suppliers.find((s) => String(s.id) === exportSupplierId)?.name || '\u2014') : '\u0423\u0441\u0456')}</div>
          <div class="meta">\u0424\u0456\u043b\u044c\u0442\u0440 \u043a\u0430\u0442\u0435\u0433\u043e\u0440\u0456\u0457: ${exportCategory || '\u0423\u0441\u0456'}</div>
          <table>
            <thead>
              <tr>${headerHtml}</tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </body>
      </html>
    `, { windowFeatures: 'width=1200,height=800' });
  };
  const groupedItems = useMemo(() => {
    let items = selected?.items || [];
    // Apply live supplier/category filter on screen
    if (exportSupplierId) {
      items = items.filter((item) => {
        const row = rows[item.id];
        const sid = row?.selectedSupplierId || (item.selectedSupplierId ? String(item.selectedSupplierId) : '');
        return sid === exportSupplierId;
      });
    }
    if (exportCategory) {
      items = items.filter((item) => item.categoryName === exportCategory);
    }
    if (!exportIncludeExcluded) {
      items = items.filter((item) => {
        const row = rows[item.id];
        return !(row?.isExcluded ?? item.isExcluded);
      });
    }
    const groups: Record<string, PurchaseWorksheetItem[]> = {};
    items.forEach((i) => {
      const cat = i.categoryName || 'Без категорії';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(i);
    });
    return groups;
  }, [selected, exportSupplierId, exportCategory, exportIncludeExcluded, rows]);
  const exportCategoryOptions = useMemo(() => {
    const set = new Set((selected?.items || []).map((i) => i.categoryName).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [selected]);
  return (
    <div className="page-shell page-shell--wide purchase-worksheet-page" style={{ maxWidth: '1500px' }}>
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-accent)', paddingBottom: '8px' }}>Закупівельний лист</h2>
      {generateError && (
        <div role="alert" style={{ marginBottom: '12px', background: 'var(--crm-danger-bg)', border: '1px solid var(--crm-danger)', color: 'var(--crm-danger)', padding: '10px 12px', borderRadius: '8px' }}>
          {generateError}
        </div>
      )}
      {!selected && (
        <div className="no-print purchase-worksheet-create-bar">
          <button onClick={() => { setGenerateError(null); setShowNewModal(true); }} style={btn('success', { marginBottom: '16px' })}>+ Новий список</button>
          {showNewModal && (
           <div className="purchase-worksheet-create-panel" style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', padding: '16px', boxShadow: 'var(--crm-shadow-sm)', marginBottom: '16px' }}>
              <h3>Новий закупівельний лист</h3>
              <div className="purchase-worksheet-create-fields" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <label>Назва:
                  <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Закупівельний лист" style={{ ...fieldStyle, display: 'block', padding: '6px', minWidth: '250px' }} />
                </label>
                <label style={{ flex: 1 }}>Примітки:
                  <input value={newNotes} onChange={(e) => setNewNotes(e.target.value)} style={{ ...fieldStyle, display: 'block', padding: '6px', width: '100%' }} />
                </label>
                <label>Основа розрахунку:
                  <select value={quantityBasis} onChange={(e) => setQuantityBasis(e.target.value as 'brutto' | 'netto' | 'output')} style={{ ...fieldStyle, display: 'block', padding: '6px', minWidth: '200px' }}>
                    <option value="brutto">Брутто</option>
                    <option value="netto">Нетто</option>
                    <option value="output">Вихід з калькуляцій</option>
                  </select>
                </label>
              </div>
              <h4>Оберіть замовлення для розрахунку потреби у продуктах:</h4>
              <div className="purchase-worksheet-create-table-scroll">
              <table className="purchase-worksheet-create-table" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px' }}>
                <thead>
                  <tr>
                    <th style={thStyle}></th>
                    <th style={thStyle}>Дата</th>
                    <th style={thStyle}>Клієнт</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Гостей</th>
                    <th style={thStyle}>Місце</th>
                    <th style={thStyle}>Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} onClick={() => toggleOrder(o.id)} style={{ cursor: 'pointer' }}>
                      <td style={tdStyle}><input type="checkbox" checked={checkedOrders.has(o.id)} onChange={() => toggleOrder(o.id)} /></td>
                      <td style={tdStyle}>{new Date(o.eventDate).toLocaleDateString('uk-UA')}</td>
                      <td style={tdStyle}>{o.customerName}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{o.guestCount}</td>
                      <td style={tdStyle}>{o.eventVenue}</td>
                      <td style={tdStyle}>{o.orderStatus}</td>
                    </tr>
                  ))}
                  {orders.length === 0 && (
                    <tr><td colSpan={6} style={{ padding: '16px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Замовлень немає</td></tr>
                  )}
                </tbody>
              </table>
              </div>
              <div className="purchase-worksheet-create-actions" style={{ display: 'flex', gap: '8px' }}>
                <button onClick={handleGenerate} disabled={generating} style={btn('success')}>{generating ? 'Складання...' : 'Скласти'}</button>
                <button onClick={() => { setGenerateError(null); setShowNewModal(false); }} style={btn('muted')}>Скасувати</button>
              </div>
            </div>
          )}
          <div className="crm-mobile-table-scroll" style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', boxShadow: 'var(--crm-shadow-sm)' }}>

            <table className="purchase-worksheet-list-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Назва</th>
                  <th style={thStyle}>Створено</th>
                  <th style={thStyle}>Статус</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Позицій</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Замовлень</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {worksheets.map((w) => (
                  <tr key={w.id}>
                    <td style={tdStyle}><a href="#!" onClick={(e) => { e.preventDefault(); openWorksheet(w.id); }}>{w.name}</a></td>
                    <td style={tdStyle}>{new Date(w.createdAt).toLocaleString('uk-UA')}</td>
                    <td style={tdStyle}>{statusLabels[w.status] || w.status}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{w.itemCount}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{w.orderCount}</td>
                    <td style={tdStyle}>{w.status === 'Draft' && <button onClick={() => handleDeleteWorksheet(w.id)} style={btn('danger', { padding: '4px 10px' })}>Видалити</button>}</td>
                  </tr>
                ))}
                {worksheets.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: '16px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>{loading ? 'Завантаження...' : 'Списків немає'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {selected && (
        <div className="print-area">
          <div className="no-print purchase-worksheet-detail-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3>{selected.name} <small style={{ color: 'var(--crm-text-muted)', fontWeight: 500 }}>({statusLabels[selected.status] || selected.status})</small></h3>
            <div className="purchase-worksheet-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {selected.status === 'Draft' && <button onClick={handleSaveChanges} disabled={saving || autoSaving} style={btn('info')}>{saving ? 'Збереження...' : 'Зберегти зміни'}</button>}
              {selected.status === 'Draft' && autoSaving && <span style={{ color: 'var(--crm-text-muted)', fontSize: '13px' }}>Автозбереження...</span>}
              {selected.status === 'Draft' && <button onClick={handleRegenerate} disabled={regenerating} style={btn('accent')}>{regenerating ? 'Переформування...' : '↻ Переформувати'}</button>}
              {selected.status === 'Draft' && <button onClick={() => handleStatusChange('Approved')} style={btn('warning')}>Погодити</button>}
              {selected.status === 'Approved' && <button onClick={handleCreateSupplierOrders} disabled={saving} style={btn('success')}>{saving ? 'Створення...' : 'Створити замовлення постачальникам'}</button>}
              {selected.status !== 'Cancelled' && <button onClick={() => handleStatusChange('Cancelled')} style={btn('danger')}>Скасувати</button>}
              {selected.status === 'Cancelled' && <button onClick={() => handleStatusChange('Draft')} style={btn('info')}>Відмінити скасування</button>}
              <button onClick={handleExportExcel} style={btn('success')}>Експорт Excel</button>
              <button onClick={handleExportText} style={btn('info')}>Експорт TXT (Viber)</button>
              <button onClick={handlePrint} style={btn('muted')}>Друк</button>
              <button onClick={closeWorksheet} style={btn('muted')}>Закрити</button>
            </div>
          </div>
          <div className="no-print responsive-split-layout purchase-worksheet-filters" style={{ marginBottom: '12px', alignItems: 'flex-end' }}>
            <label>
              Основа розрахунку
              <select value={quantityBasis} disabled={selected.status !== 'Draft'} onChange={(e) => setQuantityBasis(e.target.value as 'brutto' | 'netto' | 'output')} style={{ ...fieldStyle, display: 'block', padding: '6px', minWidth: '180px' }}>
                <option value="brutto">Брутто</option>
                <option value="netto">Нетто</option>
                <option value="output">Вихід з калькуляцій</option>
              </select>
            </label>
            <label>
              Фільтр друку/експорту: Постачальник
              <div style={{ marginTop: '4px', minWidth: '260px' }}>
                <SearchSelect
                  items={allSupplierSearchItems}
                  value={exportSupplierId}
                  onChange={setExportSupplierId}
                  placeholder="🔍 Усі / пошук постачальника..."
                  minWidth={260}
                />
              </div>
            </label>
            <label>
              Категорія
              <select value={exportCategory} onChange={(e) => setExportCategory(e.target.value)} style={{ ...fieldStyle, display: 'block', padding: '6px', minWidth: '220px' }}>
                <option value="">Усі</option>
                {exportCategoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '7px' }}>
              <input
                type="checkbox"
                checked={exportIncludeExcluded}
                onChange={(e) => setExportIncludeExcluded(e.target.checked)}
              />
              Показувати виключені позиції
            </label>
            <div className="purchase-worksheet-print-columns crm-mobile-mobile-row" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', paddingBottom: '7px' }}>
              <span style={{ fontSize: '13px', color: 'var(--crm-text)' }}>Колонки друку:</span>
              {[
                ['category', 'Категорія'],
                ['product', 'Продукт'],
                ['unit', 'Одиниця'],
                ['quantity', 'Кількість'],
                ['supplier', 'Постачальник'],
              ].map(([key, label]) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
                  <input
                    type="checkbox"
                    checked={printColumns[key as keyof typeof printColumns]}
                    onChange={(e) => setPrintColumns((prev) => ({ ...prev, [key]: e.target.checked }))}
                  />
                  {label}
                </label>
              ))}
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
              <input
                type="checkbox"
                checked={exportOnlyUnassignedSupplier}
                onChange={(e) => {
                  setExportOnlyUnassignedSupplier(e.target.checked);
                  if (e.target.checked) setExportSupplierId('');
                }}
              />
              Тільки без постачальника
              </label>
            </div>
          </div>
          {selected.notes && <p>Примітки: {selected.notes}</p>}
          <table className="purchase-worksheet-detail-table" style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--crm-surface)', color: 'var(--crm-text)', boxShadow: 'var(--crm-shadow-sm)' }}>
            <thead>
              <tr>
                <th style={thStyle} className="no-print">Вкл.</th>
                <th style={thStyle}>Продукт</th>
                <th style={thStyle}>Одиниця</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Потрібно</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Скориговано</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Залишок</th>
                <th style={thStyle}>Постачальник</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedItems).map(([category, items]) => (
                <React.Fragment key={category}>
                  <tr>
                    <td colSpan={7} className="purchase-worksheet-category-row" style={{ padding: '8px', background: 'var(--crm-surface-alt)', fontWeight: 'bold' }}>{category}</td>
                  </tr>
                  {items.map((item) => {
                    const row = rows[item.id] || {
                      adjustedQuantity: formatAdjustment(item.adjustedQuantity - item.requiredQuantity),
                      selectedSupplierId: item.selectedSupplierId ? String(item.selectedSupplierId) : '',
                      supplierQuery: item.selectedSupplierName || '',
                      isExcluded: item.isExcluded,
                      notes: item.notes,
                    };
                    const allowedSuppliers = getAllowedSuppliers(item);
                    const selectedSupplierProfile = supplierQuality.find(profile =>
                      String(profile.supplierId) === row.selectedSupplierId);
                    const rowStyle: React.CSSProperties = row.isExcluded ? { background: 'var(--crm-surface-alt)', color: 'var(--crm-text-muted)', fontStyle: 'italic' } : {};
                    return (
                      <tr key={item.id} className={row.isExcluded ? 'purchase-worksheet-excluded-row' : undefined} style={rowStyle}>
                        <td style={tdStyle} className="no-print">
                          <input
                            type="checkbox"
                            checked={!row.isExcluded}
                            disabled={selected.status !== 'Draft'}
                            onChange={(e) => updateRow(item.id, { isExcluded: !e.target.checked })}
                          />
                        </td>
                        <td style={tdStyle}>
                          <button
                            type="button"
                            onClick={() => openTraceability(item)}
                            style={{ border: 0, padding: 0, background: 'transparent', color: 'var(--crm-info)', textDecoration: 'underline', cursor: 'pointer', font: 'inherit', textAlign: 'left' }}
                            title="Показати замовлення, страви та порції"
                          >
                            {item.productName}
                          </button>
                          {row.isExcluded && <span> (виключено)</span>}
                        </td>
                        <td style={tdStyle}>{getUnitLabel(item.weightType)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{getRequiredWithAdjustment(item, row).toFixed(2)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }} className="no-print">
                          <input
                            type="text"
                            value={row.adjustedQuantity}
                            disabled={selected.status !== 'Draft'}
                            onChange={(e) => updateRow(item.id, { adjustedQuantity: e.target.value })}
                            style={{ ...fieldStyle, width: '90px', padding: '4px', textAlign: 'right' }}
                          />
                        </td>
                        <td className="print-only" style={{ ...tdStyle, textAlign: 'right', display: 'none' }}>{getRequiredWithAdjustment(item, row).toFixed(2)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{item.currentStock.toFixed(2)}</td>
                        <td style={tdStyle} className="no-print">
                          <SearchSelect
                            items={allowedSuppliers.map(s => {
                              const profile = supplierQuality.find(item => item.supplierId === s.id);
                              const risk = profile?.riskLevel === 'critical' ? '🔴' : profile?.riskLevel === 'warning' ? '🟡' : '🟢';
                              return {
                                id: String(s.id),
                                name: `${risk} ${s.name}${profile ? ` · ${profile.procurementScore.toFixed(1)}` : ''}`,
                              };
                            })}
                            value={row.selectedSupplierId}
                            disabled={selected.status !== 'Draft'}
                            onChange={(id) => {
                              const profile = supplierQuality.find(item => String(item.supplierId) === id);
                              if (profile?.riskLevel === 'critical' && !window.confirm(
                                `Постачальник має критичний ризик (рейтинг ${profile.procurementScore.toFixed(1)}). Все одно вибрати його?`
                              )) return;
                              updateRow(item.id, {
                                selectedSupplierId: id,
                                supplierQuery: suppliers.find(s => String(s.id) === id)?.name || '',
                              });
                            }}
                            placeholder="🔍 Пошук постачальника..."
                            minWidth={220}
                          />
                          {selectedSupplierProfile && selectedSupplierProfile.riskLevel !== 'normal' && (
                            <div style={{ marginTop: 3, fontSize: 10, color: selectedSupplierProfile.riskLevel === 'critical' ? 'var(--crm-danger)' : 'var(--crm-warning)' }}>
                              {selectedSupplierProfile.riskLevel === 'critical' ? 'Критичний ризик' : 'Потребує уваги'}:
                              {' '}рейтинг {selectedSupplierProfile.procurementScore.toFixed(1)}
                              {selectedSupplierProfile.overdueClaimCount > 0 ? `, прострочених претензій ${selectedSupplierProfile.overdueClaimCount}` : ''}
                              {selectedSupplierProfile.currentBlockedLotCount > 0 ? `, заблокованих партій ${selectedSupplierProfile.currentBlockedLotCount}` : ''}
                            </div>
                          )}
                        </td>
                        <td className="print-only" style={{ ...tdStyle, display: 'none' }}>
                          {suppliers.find((s) => String(s.id) === row.selectedSupplierId)?.name || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
              {filteredVisibleItems.length === 0 && (
                <tr><td colSpan={7} style={{ padding: '16px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Список порожній</td></tr>
              )}
            </tbody>
          </table>
          {traceabilityItem && (
            <div
              role="dialog"
              aria-modal="true"
              className="purchase-worksheet-traceability-modal no-print"
              style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15, 23, 42, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
              onClick={() => setTraceabilityItem(null)}
            >
              <div style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: '10px', width: 'min(980px, 100%)', maxHeight: '85vh', overflow: 'auto', padding: '20px', boxShadow: 'var(--crm-shadow-sm)' }} onClick={(event) => event.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <h3 style={{ margin: 0 }}>Розрахунок продукту: {traceabilityItem.productName}</h3>
                    <div style={{ color: 'var(--crm-text-muted)', marginTop: '4px' }}>
                      Основа: {traceability?.quantityBasis === 'netto' ? 'нетто' : traceability?.quantityBasis === 'output' ? 'вихід' : 'брутто'}
                    </div>
                  </div>
                  <button type="button" onClick={() => setTraceabilityItem(null)} style={btn('muted')}>Закрити</button>
                </div>
                {traceabilityItem.entries.length === 0 ? (
                  <p style={{ color: 'var(--crm-text-muted)' }}>Для цього продукту не знайдено деталізації у пов’язаних замовленнях.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Замовлення</th>
                        <th style={thStyle}>Дата</th>
                        <th style={thStyle}>Страва</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Порцій</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Кількість</th>
                      </tr>
                    </thead>
                    <tbody>
                      {traceabilityItem.entries.map((entry) => (
                        <tr key={`${entry.orderId}-${entry.dishName}`}>
                          <td style={tdStyle}>№{entry.orderId} · {entry.orderName || 'Без назви'}</td>
                          <td style={tdStyle}>{new Date(entry.eventDate).toLocaleDateString('uk-UA')}</td>
                          <td style={tdStyle}>{entry.dishName}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{entry.portions.toFixed(2)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{entry.quantity.toFixed(3)} {getUnitLabel(traceabilityItem.weightType)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {traceabilityLoading && <div style={{ marginTop: '10px', color: 'var(--crm-text-muted)' }}>Завантаження...</div>}
              </div>
            </div>
          )}
        </div>
      )}
      <style>{`
        .purchase-worksheet-create-bar,
        .purchase-worksheet-create-fields,
        .purchase-worksheet-create-actions,
        .purchase-worksheet-actions,
        .purchase-worksheet-filters,
        .purchase-worksheet-print-columns,
        .purchase-worksheet-detail-header {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        @media print {
          .no-print { display: none !important; }
          .print-only { display: table-cell !important; }
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; left: 0; top: 0; width: 100%; }
          .purchase-worksheet-detail-table { background: #fff !important; color: #111 !important; }
          .purchase-worksheet-category-row { background: #dfe6e9 !important; color: #111 !important; }
          .purchase-worksheet-excluded-row { background: #f2f2f2 !important; color: #555 !important; }
        }

        @media (max-width: 768px) {
          .purchase-worksheet-create-panel,
          .purchase-worksheet-detail-header {
            padding: 12px !important;
          }

          .purchase-worksheet-create-fields > label,
          .purchase-worksheet-filters > label {
            width: 100%;
          }

          .purchase-worksheet-create-fields input,
          .purchase-worksheet-create-fields select,
          .purchase-worksheet-filters select {
            min-width: 0 !important;
            width: 100%;
          }

          .purchase-worksheet-create-actions button,
          .purchase-worksheet-actions button,
          .purchase-worksheet-print-columns label {
            padding: 6px 10px !important;
            font-size: 12px !important;
          }

          .purchase-worksheet-detail-header {
            align-items: flex-start !important;
          }

          .purchase-worksheet-list-table,
          .purchase-worksheet-detail-table,
          .purchase-worksheet-create-table {
            min-width: 720px;
          }
        }
      `}</style>
    </div>
  );
};
export default PurchaseWorksheetPage;
